import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronDown } from 'react-icons/fa6';
import { CSVFileEntry } from '../../../../../types/common';

interface MetadataSectionProps {
  entry: CSVFileEntry;
  isExpanded: boolean;
  onToggle: () => void;
}

// Helper function to format numbers to 2 decimal places (strips trailing zeros)
const formatNumber = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === '') return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);
  return parseFloat(num.toFixed(2)).toString();
};

// Helper function to format UTC timestamps to dd.mm.yyyy HH:mm
const formatUTC = (utcString?: string): string => {
  if (!utcString) return '-';
  try {
    const d = new Date(utcString);
    if (isNaN(d.getTime())) return utcString;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  } catch {
    return utcString;
  }
};

export const MetadataSection: React.FC<MetadataSectionProps> = ({
  entry,
  isExpanded,
  onToggle
}) => {
  const { t } = useTranslation('training');

  const fields = [
    { label: t('csvFileUploader.headers.utcMin'), value: formatUTC(entry.utcMin), truncate: true },
    { label: t('csvFileUploader.headers.utcMax'), value: formatUTC(entry.utcMax), truncate: true },
    { label: t('csvFileUploader.headers.zeitschrittweite'), value: formatNumber(entry.zeitschrittweite) },
    { label: t('csvFileUploader.headers.offset'), value: entry.offset || '-' },
    { label: t('csvFileUploader.headers.datenpunkte'), value: entry.datenpunkte || '-' },
    { label: t('csvFileUploader.headers.numerischeDatenpunkte'), value: entry.numerischeDatenpunkte || '-' },
    { label: t('csvFileUploader.headers.numerischerAnteil'), value: entry.numerischerAnteil ? `${formatNumber(entry.numerischerAnteil)}%` : '-' },
    { label: t('csvFileUploader.headers.min'), value: formatNumber(entry.min) },
    { label: t('csvFileUploader.headers.max'), value: formatNumber(entry.max) },
  ];

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3.5 py-2.5 bg-muted/80 hover:bg-muted transition-colors duration-150"
      >
        <h3 className="text-xs font-medium text-foreground uppercase tracking-wide">
          {t('csvFileUploader.sections.metadata')}
        </h3>
        <FaChevronDown className={`w-2.5 h-2.5 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
      </button>

      {isExpanded && (
        <div className="px-3.5 py-3 bg-card border-t border-border">
          <div className="grid grid-cols-3 gap-2 items-stretch">
            {fields.map((field, idx) => (
              <div key={idx} className="flex flex-col justify-between">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide block mb-0.5">
                  {field.label}
                </span>
                <div className={`text-xs font-medium text-foreground bg-muted rounded-md px-2 py-1.5 border border-border ${field.truncate ? 'truncate' : ''}`}>
                  {field.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MetadataSection;
